import React, {memo, useCallback, useMemo, useState} from "react"
import {useHistory} from "react-router-dom"
import {useKeyboard} from "../../../../hooks/keyboard"
import {useQuestion} from "../../../../hooks/question"
import {useMessage} from "../../../../hooks/message"
import {useSelector, useDispatch} from "react-redux"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {SettingInputComponent} from "../../../../components/Settings/SettingInputComponent"
import {useLocalization} from "../../../../hooks/localization"
import {SettingSelectComponent} from "../../../../components/Settings/SettingSelectComponent"
import {SelectItem} from "../../../../components/Select"
import {useRPC} from "../../../../hooks/rpc"
import {EnergyAnalyzerModel} from "../../../../store/control/state"
import {setEnergyAnalyzer} from "../../../../store/control/actions"

const BACK_URL = "/settings"

export const EnergyPageEditScreen = memo(function EnergyPageEditScreen() {
    const {t} = useLocalization()
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard()
    const question = useQuestion()
    const {success, failure} = useMessage()
    const [rpc] = useRPC()
    const history = useHistory()
    const dispatch = useDispatch()
    const energyList = useSelector(state => state.control.energyState)
    const [daytimekWhPrice, setDaytimeKWhPrice] = useState(energyList?.day_rate)
    const [nightkWhPrice, setNightKWhPrice] = useState(energyList?.night_rate)
    const [billingDate, setBillingDate] = useState(energyList?.invoice_date)
    const [staggeredTariff, setStaggeredTariff] = useState(energyList?.device_pulse_kwh)
    const [staggeredTariffPrice, setStaggeredTariffPrice] = useState(energyList?.energy_fee)
    const [maxWarningPoint, setMaxWarningPoint] = useState(energyList?.max_point)

    const CURRENCY_LIST = useMemo<SelectItem<string>[]>(
        () => [
            {
                value: "try",
                label: "TRY (₺)"
            },
            {
                value: "usd",
                label: "USD ($)"
            },
            {
                value: "eur",
                label: "EUR (€)"
            },
            {
                value: "gbp",
                label: "GBP (£)"
            }
        ],
        []
    )
    const [currency, setCurrency] = useState<SelectItem<string>>(
        CURRENCY_LIST.find(currency => currency.value === energyList.currency_unit) || null
    )

    const onBackPressed = useCallback(() => history.push(BACK_URL), [history]) //run after saving data

    const isFieldsValid = useCallback((): boolean => {
        if (daytimekWhPrice == null) {
            failure(t("energy_settings_page.pls_enter_a_valid_fee"))

            return false
        }
        if (nightkWhPrice == null) {
            failure(t("energy_settings_page.pls_enter_a_valid_fee"))

            return false
        }
        if (staggeredTariff == null) {
            failure(t("energy_settings_page.pls_enter_gradual_tariff"))

            return false
        }
        if (staggeredTariffPrice == null) {
            failure(t("energy_settings_page.pls_enter_gradual_tariff_fee"))

            return false
        }
        if (billingDate == null || billingDate.length === 0) {
            failure(t("energy_settings_page.pls_enter_invoice_date"))

            return false
        }
        if (currency?.value == null) {
            failure(t("energy_settings_page.pls_enter_currency"))

            return false
        }
        if (maxWarningPoint == null) {
            failure(t("energy_settings_page.pls_enter_the_max_warning_value"))

            return false
        }

        return true
    }, [
        t,
        failure,
        daytimekWhPrice,
        nightkWhPrice,
        staggeredTariff,
        staggeredTariffPrice,
        billingDate,
        currency,
        maxWarningPoint
    ])

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return
        }
        const energyState: EnergyAnalyzerModel = {
            currency_unit: currency?.value,
            day_rate: daytimekWhPrice,
            device_pulse_kwh: staggeredTariff,
            energy_fee: staggeredTariffPrice,
            invoice_date: billingDate,
            night_rate: nightkWhPrice,
            max_point: maxWarningPoint
        }
        question(t("are_you_sure"), () => {
            rpc.updateEnergyInformation(energyState)
                .then(() => {
                    dispatch(setEnergyAnalyzer(energyState))
                    onBackPressed()
                    success(t("SUCCESSFULLY_EDITED"))
                })
                .catch(failure)
        })
    }, [
        isFieldsValid,
        onBackPressed,
        failure,
        success,
        question,
        dispatch,
        t,
        rpc,
        daytimekWhPrice,
        nightkWhPrice,
        staggeredTariff,
        staggeredTariffPrice,
        billingDate,
        currency,
        maxWarningPoint
    ])

    const onBlur = useCallback(() => {
        setActiveInput(null)
    }, [setActiveInput])

    const buttons = useMemo(
        () => [
            {
                text: t("temperature").toUpperCase(),
                className: "orange",
                url: "/temp-statistic"
            },
            {
                text: t("page").toUpperCase(),
                className: "blue",
                url: "/energy"
            },
            {
                text: t("save"),
                className: "green",
                onClick: onSavePressed
            },
            {
                text: t("cancel"),
                className: "red",
                url: BACK_URL
            }
        ],
        [t, onSavePressed]
    )

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className="service-edit"
                backUrl={BACK_URL}
                currentInput={currentInput}
            >
                <div className="content-divider">
                    <div className="content-wrapper">
                        {/*<SettingInputComponent
                            placeholder={t("energy_settings_page.enter_kwh_price")}
                            label={t("energy_settings_page.fee_daytime")}
                            value={daytimekWhPrice}
                            setValue={setDaytimeKWhPrice}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={t("energy_settings_page.enter_kwh_price")}
                            label={t("energy_settings_page.fee_night")}
                            value={nightkWhPrice}
                            setValue={setNightKWhPrice}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
    />*/}
                        <SettingInputComponent
                            placeholder={t("energy_settings_page.enter_kwh")}
                            label={t("energy_settings_page.device_watt_hour")}
                            value={staggeredTariff}
                            setValue={setStaggeredTariff}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={t("energy_settings_page.enter_tariff_fee")}
                            label={t("energy_settings_page.tariff_fee")}
                            value={staggeredTariffPrice}
                            setValue={setStaggeredTariffPrice}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                       {/*<SettingInputComponent
                            placeholder={t("set_date")}
                            label={t("energy_settings_page.billing_date")}
                            value={billingDate}
                            setValue={setBillingDate}
                            setActiveInput={null}
                            type="date"
/>*/}
                        <SettingSelectComponent
                            label={t("energy_settings_page.currency")}
                            options={CURRENCY_LIST}
                            onChange={setCurrency}
                            value={currency}
                        />
                        <SettingInputComponent
                            placeholder={t("energy_settings_page.enter_max_kwh")}
                            label={t("energy_settings_page.max_kwh")}
                            value={maxWarningPoint}
                            setValue={setMaxWarningPoint}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                    </div>
                    <div className="content-wrapper"></div>
                </div>
            </SettingsPageContainer>
            {renderKeyboard()}
        </PasswordContainer>
    )
})
